import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Layout, Typography, Input, InputNumber, Button, Tooltip, Avatar, Empty,
  Spin, Alert, Tree, Tag, Select, Switch, message,
} from 'antd';
import {
  UserOutlined, LogoutOutlined, LockOutlined, UnlockOutlined,
  SaveOutlined,
} from '@ant-design/icons';
import EnvVersionLabel from './Envversionlabel';

const { Header, Sider, Content } = Layout;
const { Title, Text } = Typography;

// Both files are served from /public/rules/.
const PIPELINE_CONFIG_URL = '/rules/pipeline_config.json';
const PROSPECTIVE_HCC_URL = '/rules/prospective_hcc.json';

// Stable Tree keys for the four left-hand categories.
const CAT_THREADING       = 'Threading';
const CAT_LLM_MODELS      = 'LLM Models';
const CAT_PIPELINE_STEPS  = 'Agentic Workflow Steps';
const PARAMS_PARENT_TITLE = 'Configurable Parameters';
const PARAM_KEY_PREFIX    = 'params::';   // params::<step_name>

const EDITABLE_ROLES = ['hcc_IT'];



// snake_case → Title Case ("section_boundary_detection" → "Section Boundary Detection").
// Acronyms (NER, LLM, ID, OCR) get preserved in upper-case.
const ACRONYMS = new Set(['ner', 'llm', 'id', 'ocr', 'icd', 'hcc', 'api', 'sql', 'rx']);
const humanize = (s) =>
  String(s || '')
    .split('_')
    .map(w => !w ? '' : ACRONYMS.has(w.toLowerCase())
      ? w.toUpperCase()
      : w[0].toUpperCase() + w.slice(1))
    .join(' ');

/**
 * HCCRuntimeConfig
 *
 * Tree-on-left, entries-on-right editor for the HCC pipeline runtime config.
 * Shell mirrors FocusedConditionsConfig — same Sider, Header, gradient
 * Content background, lock/unlock toggle, and placeholder Save.
 *
 * Data is loaded in parallel from:
 *   - /rules/pipeline_config.json  → threading + configurable_parameters
 *   - /rules/prospective_hcc.json  → llm_models + pipeline_steps
 *
 * Left tree (4 categories):
 *   • Threading              — num_chart_threads, num_encounter_threads
 *   • LLM Models             — every key in llm_models
 *   • Pipeline Steps         — every key in pipeline_steps
 *   • Configurable Parameters (parent)
 *       └─ one child per unique step_name (humanized, ordered by step_id)
 *
 * Right pane: per-category card variant.
 *   - Threading / Configurable Parameters → InputNumber
 *   - LLM Models                          → Select (free-text + autocomplete)
 *   - Pipeline Steps                      → Switch
 *
 * All edits are in-memory. Save is a placeholder (no backend yet).
 */
const HCCRuntimeConfig = ({
  username,
  sessionId,
  userRole,
  userFeatures,
  tabOrder,
  onLogout,
}) => {
  const navigate = useNavigate();

  // ── State ────────────────────────────────────────────────────────
  const [loading, setLoading]     = useState(true);
  const [loadError, setLoadError] = useState(null);

  // Threading: { num_chart_threads, num_encounter_threads }
  const [threading, setThreading] = useState({});

  // LLM models: { ner_extraction: 'gpt-5-mini', ... } — order preserved via separate keys array.
  const [llmModels, setLlmModels]       = useState({});
  const [llmModelKeys, setLlmModelKeys] = useState([]);

  // Pipeline steps: { enable_post_ner_correction: true, ... }
  const [pipelineSteps, setPipelineSteps]       = useState({});
  const [pipelineStepKeys, setPipelineStepKeys] = useState([]);

  // Configurable parameters: array of rich objects, each with an injected _id.
  const [params, setParams] = useState([]);

  const [selectedKey, setSelectedKey] = useState(CAT_THREADING);
  const [editMode, setEditMode]       = useState(false);

  const canEdit = EDITABLE_ROLES.includes(userRole);


  // ── Load + parse ─────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;

    const fetchJson = (url) =>
      fetch(url).then(res => {
        if (!res.ok) throw new Error(`${url} → HTTP ${res.status}`);
        return res.json();
      });

    Promise.all([fetchJson(PIPELINE_CONFIG_URL), fetchJson(PROSPECTIVE_HCC_URL)])
      .then(([pipelineCfg, prospective]) => {
        if (cancelled) return;

        // Threading
        const pipe = pipelineCfg.pipeline || {};
        setThreading({
          num_chart_threads:     pipe.num_chart_threads ?? 0,
          num_encounter_threads: pipe.num_encounter_threads ?? 0,
        });

        // Configurable parameters (preserve file order; assign stable ids).
        const rawParams = Array.isArray(pipelineCfg.configurable_parameters)
          ? pipelineCfg.configurable_parameters
          : [];
        setParams(rawParams.map((p, i) => ({ ...p, _id: `param-${i}` })));

        // LLM models — preserve insertion order via Object.keys.
        const models = prospective.llm_models || {};
        setLlmModels({ ...models });
        setLlmModelKeys(Object.keys(models));

        // Pipeline steps
        const steps = prospective.pipeline_steps || {};
        setPipelineSteps({ ...steps });
        setPipelineStepKeys(Object.keys(steps));
      })
      .catch(err => {
        if (cancelled) return;
        console.error('Failed to load pipeline config files:', err);
        setLoadError(err.message || 'Unknown error');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, []);

  // ── Derived: tree + selected slice ───────────────────────────────
  // Unique step_name list, ordered by their lowest step_id. Each becomes
  // a leaf under the "Configurable Parameters" parent.
  const paramStepNames = useMemo(() => {
    const minStepId = new Map();
    params.forEach(p => {
      const existing = minStepId.get(p.step_name);
      if (existing === undefined || p.step_id < existing) {
        minStepId.set(p.step_name, p.step_id);
      }
    });
    return Array.from(minStepId.keys()).sort(
      (a, b) => minStepId.get(a) - minStepId.get(b)
    );
  }, [params]);

  const treeData = useMemo(() => [
    { title: CAT_THREADING,      key: CAT_THREADING,      isLeaf: true },
    { title: CAT_LLM_MODELS,     key: CAT_LLM_MODELS,     isLeaf: true },
    { title: CAT_PIPELINE_STEPS, key: CAT_PIPELINE_STEPS, isLeaf: true },
    {
      title:      PARAMS_PARENT_TITLE,
      key:        `__parent::${PARAMS_PARENT_TITLE}`,
      selectable: false,
      children: paramStepNames.map(sn => ({
        title:  humanize(sn),
        key:    `${PARAM_KEY_PREFIX}${sn}`,
        isLeaf: true,
      })),
    },
  ], [paramStepNames]);

  const defaultExpandedKeys = useMemo(
    () => treeData.filter(n => !n.isLeaf).map(n => n.key),
    [treeData]
  );

  // Autocomplete options for the LLM model Select — every distinct value
  // already in the loaded config.
  const knownModels = useMemo(() => {
    const s = new Set();
    Object.values(llmModels).forEach(v => { if (v) s.add(v); });
    return Array.from(s).sort();
  }, [llmModels]);

  // Total entry count for the sider footer.
  const totalEntries =
    Object.keys(threading).length +
    llmModelKeys.length +
    pipelineStepKeys.length +
    params.length;

  // ── Handlers ─────────────────────────────────────────────────────
  const handleLockToggle = () => setEditMode(prev => !prev);

  const handleSave = () => {
    message.info('Save is not yet wired up — edits are in memory only.');
  };

  const handleTreeSelect = (selectedKeys) => {
    if (!selectedKeys || selectedKeys.length === 0) return;
    const key = selectedKeys[0];
    if (typeof key === 'string' && !key.startsWith('__parent::')) {
      setSelectedKey(key);
    }
  };

  const updateThreading = (field, value) =>
    setThreading(prev => ({ ...prev, [field]: value }));

  const updateLlmModel = (key, value) =>
    setLlmModels(prev => ({ ...prev, [key]: value }));

  const updatePipelineStep = (key, value) =>
    setPipelineSteps(prev => ({ ...prev, [key]: value }));

  const updateParam = (id, patch) =>
    setParams(prev => prev.map(p => (p._id === id ? { ...p, ...patch } : p)));

  // ── Header for the right-hand pane ───────────────────────────────
  const rightPaneHeader = useMemo(() => {
    if (selectedKey === CAT_THREADING) {
      return {
        title:       CAT_THREADING,
        subLabel:    'pipeline',
        countLabel:  `${Object.keys(threading).length} settings`,
      };
    }
    if (selectedKey === CAT_LLM_MODELS) {
      return {
        title:      CAT_LLM_MODELS,
        subLabel:   'pipeline',
        countLabel: `${llmModelKeys.length} models`,
      };
    }
    if (selectedKey === CAT_PIPELINE_STEPS) {
      return {
        title:      CAT_PIPELINE_STEPS,
        subLabel:   'pipeline',
        countLabel: `${pipelineStepKeys.length} steps`,
      };
    }
    if (selectedKey.startsWith(PARAM_KEY_PREFIX)) {
      const stepName = selectedKey.slice(PARAM_KEY_PREFIX.length);
      const count = params.filter(p => p.step_name === stepName).length;
      return {
        title:      humanize(stepName),
        subLabel:   PARAMS_PARENT_TITLE,
        countLabel: `${count} ${count === 1 ? 'parameter' : 'parameters'}`,
      };
    }
    return { title: '', subLabel: '', countLabel: '' };
  }, [selectedKey, threading, llmModelKeys, pipelineStepKeys, params]);

  // ── Right-pane content for the current selection ─────────────────
  const renderRightPaneBody = () => {
    if (selectedKey === CAT_THREADING) {
      const fields = [
        {
          name: 'num_chart_threads',
          label: 'Chart-level threads',
          description: 'Number of charts processed in parallel by the pipeline orchestrator.',
        },
        {
          name: 'num_encounter_threads',
          label: 'Encounter-level threads',
          description: 'Number of encounters processed in parallel within a single chart.',
        },
      ];
      return fields.map(f => (
        <NumericSettingCard
          key={f.name}
          variable={f.name}
          label={f.label}
          description={f.description}
          value={threading[f.name]}
          editMode={editMode}
          onChange={(v) => updateThreading(f.name, v)}
          min={1}
        />
      ));
    }

    if (selectedKey === CAT_LLM_MODELS) {
      if (llmModelKeys.length === 0) {
        return <Empty description="No LLM models configured." />;
      }
      return llmModelKeys.map(key => (
        <LlmModelCard
          key={key}
          variable={key}
          value={llmModels[key]}
          editMode={editMode}
          knownModels={knownModels}
          onChange={(v) => updateLlmModel(key, v)}
        />
      ));
    }

    if (selectedKey === CAT_PIPELINE_STEPS) {
      if (pipelineStepKeys.length === 0) {
        return <Empty description="No pipeline steps configured." />;
      }
      return pipelineStepKeys.map(key => (
        <PipelineStepCard
          key={key}
          variable={key}
          value={pipelineSteps[key]}
          editMode={editMode}
          onChange={(v) => updatePipelineStep(key, v)}
        />
      ));
    }

    if (selectedKey.startsWith(PARAM_KEY_PREFIX)) {
      const stepName = selectedKey.slice(PARAM_KEY_PREFIX.length);
      const rows = params.filter(p => p.step_name === stepName);
      if (rows.length === 0) {
        return <Empty description="No parameters in this step." />;
      }
      return rows.map(p => (
        <ConfigurableParameterCard
          key={p._id}
          param={p}
          editMode={editMode}
          onChange={(patch) => updateParam(p._id, patch)}
        />
      ));
    }

    return null;
  };

  // ── Render ───────────────────────────────────────────────────────
  return (
    <Layout style={{ minHeight: '100vh' }}>

      {/* ── Sider (collapsed rail, mirrors FocusedConditionsConfig) ── */}
      <Sider
        width={60}
        collapsible
        collapsed={true}
        collapsedWidth={60}
        trigger={null}
        className="sidebar-gradient"
        style={{
          display: 'flex',
          flexDirection: 'column',
          height: '100vh',
          position: 'fixed',
          left: 0, top: 0, bottom: 0,
          zIndex: 10,
        }}
      >
        <div style={{
          display: 'flex',
          background: 'linear-gradient(180deg, #162349 0%, #2a4a7c 100%)',
          flexDirection: 'column',
          justifyContent: 'space-between',
          height: '100%',
          alignItems: 'center',
          padding: '20px 0',
        }}>
          <div></div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16, alignItems: 'center' }}>
            <Tooltip title={username} placement="right">
              <Avatar
                style={{ backgroundColor: 'white', color: '#162349', cursor: 'pointer', border: '2px solid white' }}
                icon={<UserOutlined />}
              />
            </Tooltip>
            <Tooltip title="Logout" placement="right">
              <Button
                type="text"
                icon={<LogoutOutlined style={{ color: 'white' }} />}
                onClick={onLogout}
                style={{ color: 'white' }}
              />
            </Tooltip>
            <EnvVersionLabel style={{ color: 'rgba(255,255,255,0.6)', fontSize: 12, fontWeight: 'Bold' }} />
          </div>
        </div>
      </Sider>

      <Layout style={{ marginLeft: 60 }}>

        {/* ── Header (breadcrumb + lock/save) ──────────────────── */}
        <Header style={{
          background: '#162349',
          padding: '0 20px',
          height: 64,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexShrink: 0,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, overflow: 'hidden' }}>
            <span
              onClick={() => navigate('/hcc-config')}
              style={{
                color: 'rgba(255,255,255,0.75)',
                fontSize: 20, fontWeight: 700,
                cursor: 'pointer', whiteSpace: 'nowrap',
                transition: 'color 0.15s',
              }}
              onMouseEnter={e => (e.currentTarget.style.color = 'white')}
              onMouseLeave={e => (e.currentTarget.style.color = 'rgba(255,255,255,0.75)')}
            >
              HCC STUDIO
            </span>
            <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: 20 }}>/</span>
            <span style={{
              color: 'white',
              fontSize: 20, fontWeight: 700,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>
              Agentic Runtime Configuration
            </span>
          </div>

          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {canEdit ? (
              <>
                <Tooltip title={editMode ? 'Click to lock (return to view-only)' : 'Click to unlock for editing'}>
                  <Button
                    icon={editMode ? <UnlockOutlined /> : <LockOutlined />}
                    onClick={handleLockToggle}
                    style={{
                      backgroundColor: editMode ? '#FEF3C7' : 'white',
                      color: '#162349',
                      fontWeight: 600,
                      borderColor: editMode ? '#F59E0B' : '#d9d9d9',
                    }}
                  >
                    {editMode ? 'Unlocked' : 'Locked'}
                  </Button>
                </Tooltip>
                <Tooltip title={editMode ? 'Save (not yet wired up)' : 'Unlock to enable saving'}>
                  <Button
                    icon={<SaveOutlined />}
                    onClick={handleSave}
                    disabled={!editMode}
                    style={{ backgroundColor: 'white', color: '#162349', fontWeight: 600 }}
                  >
                    Save
                  </Button>
                </Tooltip>
              </>
            ) : (
              <Tag
                style={{
                  backgroundColor: 'rgba(255,255,255,0.15)',
                  color: 'white',
                  border: '1px solid rgba(255,255,255,0.3)',
                  fontWeight: 600,
                  fontSize: 13,
                  padding: '4px 12px',
                }}
              >
                Display Only
              </Tag>
            )}
          </div>

        </Header>

        {/* ── Content ─────────────────────────────────────────── */}
        <Content style={{
          padding: '28px 32px',
          background: 'linear-gradient(to bottom, #f8f9fa 0%, #f0f2f5 33%, #ced4da 90%, #ced4da 100%)',
          minHeight: 'calc(100vh - 64px)',
        }}>

          {loading && (
            <div style={{ textAlign: 'center', padding: '80px 0' }}>
              <Spin size="large" />
              <div style={{ marginTop: 16, color: '#6B7280', fontSize: 14 }}>
                Loading pipeline configuration…
              </div>
            </div>
          )}

          {!loading && loadError && (
            <Alert
              type="warning"
              showIcon
              message="Configuration files could not be loaded"
              description={
                <span>
                  Tried fetching <code>{PIPELINE_CONFIG_URL}</code> and{' '}
                  <code>{PROSPECTIVE_HCC_URL}</code>. ({loadError})
                </span>
              }
              style={{ maxWidth: 640, margin: '40px auto' }}
            />
          )}

          {!loading && !loadError && (
            <div style={{
              background: 'white',
              borderRadius: 8,
              boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
              overflow: 'hidden',
              display: 'flex',
              minHeight: 'calc(100vh - 64px - 56px)',
            }}>

              {/* ── Left: tree ──────────────────────────────── */}
              <div style={{
                width: 320,
                flexShrink: 0,
                borderRight: '1px solid #E5E7EB',
                display: 'flex',
                flexDirection: 'column',
                background: '#FAFAFA',
              }}>
                <div style={{ padding: '14px 16px', borderBottom: '1px solid #E5E7EB' }}>
                  <Text style={{
                    fontSize: 11, fontWeight: 700, color: '#6B7280',
                    letterSpacing: '0.08em', textTransform: 'uppercase',
                  }}>
                    Configuration sections
                  </Text>
                </div>
                <div style={{ flex: 1, overflowY: 'auto', padding: '8px 8px 16px 8px' }}>
                  <Tree
                    treeData={treeData}
                    defaultExpandedKeys={defaultExpandedKeys}
                    selectedKeys={selectedKey ? [selectedKey] : []}
                    onSelect={handleTreeSelect}
                    blockNode
                  />
                </div>
                <div style={{ padding: '10px 16px', borderTop: '1px solid #E5E7EB', background: 'white' }}>
                  <Text style={{ fontSize: 11, color: '#9CA3AF' }}>
                    {3 + paramStepNames.length} sections, {totalEntries} settings total
                  </Text>
                </div>
              </div>

              {/* ── Right: entries for selected section ─────── */}
              <div style={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
              }}>
                {!selectedKey ? (
                  <div style={{ padding: '80px 0', textAlign: 'center' }}>
                    <Empty description="Select a section on the left." />
                  </div>
                ) : (
                  <>
                    {/* Section title row */}
                    <div style={{
                      padding: '20px 28px 12px 28px',
                      borderBottom: '1px solid #E5E7EB',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      gap: 16,
                    }}>
                      <div style={{ overflow: 'hidden' }}>
                        <Text style={{
                          fontSize: 11, fontWeight: 700, color: '#9CA3AF',
                          letterSpacing: '0.08em', textTransform: 'uppercase',
                        }}>
                          {rightPaneHeader.subLabel}
                        </Text>
                        <Title level={4} style={{ margin: '2px 0 0 0', color: '#111827' }}>
                          {rightPaneHeader.title}
                        </Title>
                        <Text style={{ fontSize: 12, color: '#6B7280' }}>
                          {rightPaneHeader.countLabel}
                        </Text>
                      </div>
                    </div>

                    {/* Entries list */}
                    <div style={{
                      flex: 1,
                      overflowY: 'auto',
                      padding: '16px 28px 28px 28px',
                    }}>
                      {renderRightPaneBody()}
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

        </Content>
      </Layout>
    </Layout>
  );
};

// ────────────────────────────────────────────────────────────────────
// Card variants. Each mirrors the visual language of the original
// EntryCard from FocusedConditionsConfig: a rounded white panel with a
// prominent monospace identifier badge on the top row, a small grey
// "type" tag next to it, a DESCRIPTION block beneath, and a VALUE block
// at the bottom that flips between read-only display and editable input.
// ────────────────────────────────────────────────────────────────────

// Reusable shell — common border, padding, top row scaffolding.
const CardShell = ({ children }) => (
  <div style={{
    border: '1px solid #E5E7EB',
    borderRadius: 8,
    padding: '16px 20px',
    marginBottom: 14,
    background: '#FFFFFF',
    transition: 'box-shadow 0.12s, border-color 0.12s',
  }}>
    {children}
  </div>
);

const VariableBadge = ({ children }) => (
  <Text style={{
    fontFamily: 'monospace',
    fontWeight: 700,
    fontSize: 15,
    color: '#162349',
    padding: '2px 8px',
    background: '#EEF2FF',
    borderRadius: 4,
    border: '1px solid #C7D2FE',
  }}>
    {children}
  </Text>
);

const KindTag = ({ children }) => (
  <Tag style={{
    fontSize: 12,
    color: '#374151',
    background: '#F3F4F6',
    border: '1px solid #E5E7EB',
  }}>
    {children}
  </Tag>
);

const SectionLabel = ({ children, style }) => (
  <Text style={{
    fontSize: 10, fontWeight: 700, color: '#9CA3AF',
    letterSpacing: '0.08em', textTransform: 'uppercase',
    display: 'block', marginBottom: 4,
    ...style,
  }}>
    {children}
  </Text>
);

// ── 1. Threading: numeric setting ────────────────────────────────────
const NumericSettingCard = ({ variable, label, description, value, editMode, onChange, min }) => (
  <CardShell>
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: 12,
      marginBottom: 14,
      flexWrap: 'wrap',
    }}>
      <VariableBadge>{variable}</VariableBadge>
      <KindTag>threading</KindTag>
    </div>

    <div style={{ marginBottom: 14 }}>
      <SectionLabel>Description</SectionLabel>
      <Text style={{ fontSize: 14, color: '#1F2937' }}>
        <strong>{label}.</strong> {description}
      </Text>
    </div>

    <div>
      <SectionLabel>Value</SectionLabel>
      {editMode ? (
        <InputNumber
          value={value}
          onChange={onChange}
          min={min ?? 1}
          style={{ width: 140 }}
        />
      ) : (
        <Text style={{
          fontFamily: 'monospace', fontSize: 15, color: '#111827', fontWeight: 600,
        }}>
          {value ?? '—'}
        </Text>
      )}
    </div>
  </CardShell>
);

// ── 2. LLM Models: string setting with autocomplete ─────────────────
const LlmModelCard = ({ variable, value, editMode, knownModels, onChange }) => (
  <CardShell>
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: 12,
      marginBottom: 14,
      flexWrap: 'wrap',
    }}>
      <VariableBadge>{variable}</VariableBadge>
      <KindTag>llm model</KindTag>
    </div>

    <div>
      <SectionLabel>Model</SectionLabel>
      {editMode ? (
        <Select
          value={value || undefined}
          onChange={onChange}
          showSearch
          allowClear
          placeholder="e.g. gpt-5-mini"
          style={{ width: 280 }}
          options={knownModels.map(m => ({ value: m, label: m }))}
          // Allow free-text values not in the autocomplete list.
          onBlur={(e) => {
            const v = e.target.value;
            if (v && v !== value) onChange(v);
          }}
        />
      ) : (
        <Text style={{
          fontFamily: 'monospace', fontSize: 14, color: '#1F2937',
        }}>
          {value || (
            <span style={{ color: '#D1D5DB', fontStyle: 'italic' }}>(unset)</span>
          )}
        </Text>
      )}
    </div>
  </CardShell>
);

// ── 3. Pipeline Steps: boolean toggle ───────────────────────────────
const PipelineStepCard = ({ variable, value, editMode, onChange }) => (
  <CardShell>
    <div style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 12,
      flexWrap: 'wrap',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <VariableBadge>{variable}</VariableBadge>
        <KindTag>pipeline step</KindTag>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <Text style={{
          fontSize: 13,
          color: value ? '#059669' : '#9CA3AF',
          fontWeight: 600,
        }}>
          {value ? 'Enabled' : 'Disabled'}
        </Text>
        <Switch
          checked={!!value}
          disabled={!editMode}
          onChange={onChange}
        />
      </div>
    </div>
  </CardShell>
);

// ── 4. Configurable Parameters: rich card ──────────────────────────
const ConfigurableParameterCard = ({ param, editMode, onChange }) => {
  const isNumericDefault = typeof param.default_value === 'number';

  return (
    <CardShell>
      {/* Top row: variable + step tag */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        marginBottom: 10,
        flexWrap: 'wrap',
      }}>
        <VariableBadge>{param.variable}</VariableBadge>
        <KindTag>{humanize(param.step_name)}</KindTag>
        <Text style={{ fontSize: 11, color: '#9CA3AF' }}>
          step {param.step_id}
        </Text>
      </div>

      {/* Friendly name */}
      <Title level={5} style={{ margin: '2px 0 10px 0', color: '#111827' }}>
        {param.name}
      </Title>

      {/* Description */}
      <div style={{ marginBottom: 14 }}>
        <SectionLabel>Description</SectionLabel>
        <Text style={{ fontSize: 13, color: '#4B5563', lineHeight: 1.55 }}>
          {param.description}
        </Text>
      </div>

      {/* Default value */}
      <div style={{ marginBottom: 14 }}>
        <SectionLabel>Default value</SectionLabel>
        {editMode ? (
          isNumericDefault ? (
            <InputNumber
              value={param.default_value}
              onChange={(v) => onChange({ default_value: v })}
              style={{ width: 200 }}
            />
          ) : (
            <Input
              value={String(param.default_value ?? '')}
              onChange={(e) => onChange({ default_value: e.target.value })}
              style={{ width: 280 }}
            />
          )
        ) : (
          <Text style={{
            fontFamily: 'monospace', fontSize: 15, color: '#111827', fontWeight: 600,
          }}>
            {String(param.default_value ?? '—')}
          </Text>
        )}
      </div>

      {/* Source location */}
      {param.source_location && (
        <div>
          <SectionLabel>Source location</SectionLabel>
          <Text style={{
            fontFamily: 'monospace',
            fontSize: 11,
            color: '#6B7280',
            wordBreak: 'break-word',
          }}>
            {param.source_location}
          </Text>
        </div>
      )}
    </CardShell>
  );
};

export default HCCRuntimeConfig;